import { ACADEMY_API_BASE, AcademyApiError, requestAcademy } from "./academy-api";

export interface AcademyUser {
  id: string;
  name: string;
  email: string;
  emailVerified: boolean;
  accountType?: string;
  mfaRequired?: boolean;
}
export interface AcademyWorkspace {
  id: string;
  name: string;
  type: string;
  role: string;
  status: "active" | "onboarding" | "invited" | "requested";
  permissions?: string[];
}
export interface AcademyBootstrap {
  user: AcademyUser | null;
  workspaces: AcademyWorkspace[];
  selectedWorkspaceId?: string | null;
  expiresAt?: string | number;
  csrfToken?: string;
  requiresMfa?: boolean;
}
export type VerifiedAudience = "learners" | "parents";
export interface VerifiedSession {
  version: 1;
  subject: string;
  name: string;
  email: string;
  audience: VerifiedAudience;
  role: string;
  workspaceId: string;
  permissions: string[];
  issuedAt: number;
  expiresAt: number;
  csrfToken?: string;
}
export class AcademyAuthenticationError extends AcademyApiError {
  constructor(message: string, status = 403, code = "ACCESS_REQUIRED") {
    super(message, status, code);
    this.name = "AcademyAuthenticationError";
  }
}
const object = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === "object" && !Array.isArray(value));
const nonempty = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

export function validateBootstrap(value: unknown): AcademyBootstrap {
  const invalid = () =>
    new AcademyAuthenticationError("Academy returned an invalid session response.", 502, "INVALID_RESPONSE");
  if (!object(value) || !("user" in value) || !Array.isArray(value.workspaces)) throw invalid();
  if (
    value.user !== null &&
    (!object(value.user) ||
      !nonempty(value.user.id) ||
      !nonempty(value.user.name) ||
      !nonempty(value.user.email) ||
      typeof value.user.emailVerified !== "boolean" ||
      (value.user.mfaRequired !== undefined && typeof value.user.mfaRequired !== "boolean"))
  )
    throw invalid();
  const ids = new Set<string>();
  for (const workspace of value.workspaces) {
    if (
      !object(workspace) ||
      !nonempty(workspace.id) ||
      ids.has(workspace.id) ||
      !nonempty(workspace.name) ||
      !nonempty(workspace.type) ||
      !nonempty(workspace.role) ||
      !["active", "onboarding", "invited", "requested"].includes(String(workspace.status)) ||
      (workspace.permissions !== undefined &&
        (!Array.isArray(workspace.permissions) || !workspace.permissions.every(nonempty)))
    )
      throw invalid();
    ids.add(workspace.id);
  }
  if (
    (value.selectedWorkspaceId !== undefined &&
      value.selectedWorkspaceId !== null &&
      !nonempty(value.selectedWorkspaceId)) ||
    (value.csrfToken !== undefined && !nonempty(value.csrfToken)) ||
    (value.requiresMfa !== undefined && typeof value.requiresMfa !== "boolean") ||
    (value.expiresAt !== undefined &&
      !(typeof value.expiresAt === "number" && Number.isFinite(value.expiresAt)) &&
      !(typeof value.expiresAt === "string" && Number.isFinite(Date.parse(value.expiresAt))))
  )
    throw invalid();
  return value as unknown as AcademyBootstrap;
}

export async function loadAcademySession(signal?: AbortSignal) {
  return validateBootstrap(await requestAcademy(ACADEMY_API_BASE, "session/bootstrap", { signal }));
}
export function workspaceMatches(workspace: AcademyWorkspace, audience: VerifiedAudience) {
  return (
    workspace.status === "active" &&
    (audience === "learners" ? ["learner", "team-learner"] : ["parent", "guardian"]).includes(workspace.role)
  );
}
export function sessionFromBootstrap(
  result: AcademyBootstrap,
  audience: VerifiedAudience,
  preferredWorkspaceId?: string,
): VerifiedSession | null {
  validateBootstrap(result);
  if (!result.user) return null;
  if (!result.user.emailVerified || result.requiresMfa)
    throw new AcademyAuthenticationError(
      "Complete your account verification before opening this workspace.",
      403,
      "VERIFICATION_REQUIRED",
    );
  const workspaces = result.workspaces.filter(workspace => workspaceMatches(workspace, audience));
  if (
    preferredWorkspaceId &&
    result.selectedWorkspaceId &&
    preferredWorkspaceId !== result.selectedWorkspaceId
  )
    throw new AcademyAuthenticationError(
      "Your selected workspace changed. Open the current workspace before continuing.",
      403,
      "WORKSPACE_CHANGED",
    );
  const requested = preferredWorkspaceId ?? result.selectedWorkspaceId;
  const workspace =
    workspaces.find(item => item.id === requested) ??
    (!preferredWorkspaceId && workspaces.length === 1 ? workspaces[0] : undefined);
  if (!workspace)
    throw new AcademyAuthenticationError(
      workspaces.length > 1
        ? "Choose your workspace in Academy Access."
        : `Your account has no active ${audience === "parents" ? "parent or guardian" : "learner"} workspace.`,
      403,
      workspaces.length > 1 ? "WORKSPACE_SELECTION_REQUIRED" : "WORKSPACE_ACCESS_REQUIRED",
    );
  const now = Date.now();
  const expiry =
    typeof result.expiresAt === "number"
      ? result.expiresAt
      : typeof result.expiresAt === "string"
        ? Date.parse(result.expiresAt)
        : now + 60_000;
  if (expiry <= now) return null;
  return {
    version: 1,
    subject: result.user.id,
    name: result.user.name,
    email: result.user.email,
    audience,
    role: workspace.role,
    workspaceId: workspace.id,
    permissions: [...(workspace.permissions ?? [])],
    issuedAt: now,
    expiresAt: Math.min(expiry, now + 60_000),
    csrfToken: result.csrfToken,
  };
}

export const AUTH_CHANGE_KEY = "chatoner-academy:session-change:v1";
/** This signal can only lock views; receiving it never grants an identity. */
export function announceSessionChange() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(AUTH_CHANGE_KEY, crypto.randomUUID());
  } catch {
    /* Cookies do not depend on local storage. */
  }
  window.dispatchEvent(new Event("academy:identity-changing"));
}
export function createRoleAuthentication(audience: VerifiedAudience, accessUrl: () => string) {
  let verifiedSession: VerifiedSession | null = null;
  let generation = 0;
  let blocked = false;
  let lastCsrfToken: string | undefined;
  let inFlight: Promise<VerifiedSession | null> | null = null;
  const stateKey = `chatoner-academy:${audience}:authorization-state`;
  const lockKey = `chatoner-academy:${audience}:signed-out`;
  const dispatch = (name: string, detail?: unknown) => {
    if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent(name, { detail }));
  };
  function clearSession() {
    generation++;
    verifiedSession = null;
    inFlight = null;
    dispatch("academy:session", null);
  }
  function getSession() {
    return verifiedSession && verifiedSession.expiresAt > Date.now() && !blocked ? verifiedSession : null;
  }
  function readStored(key: string) {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }
  function validateReturn() {
    const url = new URL(window.location.href);
    if (
      !url.searchParams.has("auth_code") &&
      !url.searchParams.has("state") &&
      url.searchParams.get("auth") !== "verified"
    )
      return;
    const valid =
      !url.searchParams.has("auth_code") &&
      url.searchParams.get("auth") === "verified" &&
      Boolean(readStored(stateKey)) &&
      url.searchParams.get("state") === readStored(stateKey);
    try {
      window.sessionStorage.removeItem(stateKey);
      if (valid) {
        window.sessionStorage.removeItem(lockKey);
      }
    } catch {
      /* A server bootstrap still determines identity. */
    }
    ["auth_code", "state", "auth", "workspace"].forEach(key => url.searchParams.delete(key));
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
    if (!valid)
      throw new AcademyAuthenticationError(
        "This sign-in return is invalid. Open Academy Access and sign in again.",
        401,
        "INVALID_CALLBACK",
      );
    blocked = false;
  }
  function redirectToSignIn() {
    clearSession();
    const state = crypto.randomUUID();
    try {
      window.sessionStorage.setItem(stateKey, state);
    } catch {
      throw new AcademyAuthenticationError(
        "Allow session storage in this browser to complete sign-in.",
        0,
        "STORAGE_UNAVAILABLE",
      );
    }
    const target = new URL(accessUrl(), window.location.origin);
    target.searchParams.set("client_id", audience);
    target.searchParams.set("return_to", window.location.href);
    target.searchParams.set("state", state);
    window.location.assign(target.toString());
  }
  async function requireFrontendSession(): Promise<VerifiedSession | null> {
    if (typeof window === "undefined") return null;
    try {
      window.localStorage.removeItem(`chatoner-academy:${audience}:session:v1`);
    } catch {
      /* Legacy data is never read. */
    }
    validateReturn();
    if (blocked || readStored(lockKey))
      throw new AcademyAuthenticationError(
        "This workspace is locked. Sign in again to continue.",
        401,
        "SIGNED_OUT",
      );
    if (inFlight) return inFlight;
    const started = generation;
    const pending = (async () => {
      try {
        const result = await loadAcademySession();
        if (started !== generation || blocked) return null;
        lastCsrfToken = result.csrfToken;
        const session = sessionFromBootstrap(result, audience);
        if (!session)
          throw new AcademyAuthenticationError(
            "Sign in to open your Academy workspace.",
            401,
            "SIGN_IN_REQUIRED",
          );
        verifiedSession = session;
        dispatch("academy:session", session);
        return session;
      } catch (error) {
        if (started !== generation || blocked) return null;
        verifiedSession = null;
        dispatch("academy:session", null);
        throw error;
      } finally {
        if (started === generation) inFlight = null;
      }
    })();
    inFlight = pending;
    return pending;
  }
  async function signOut() {
    const csrfToken = verifiedSession?.csrfToken ?? lastCsrfToken;
    blocked = true;
    try {
      window.sessionStorage.setItem(lockKey, "1");
    } catch {
      /* The in-memory lock remains effective. */
    }
    dispatch("academy:signing-out");
    clearSession();
    announceSessionChange();
    try {
      await requestAcademy(ACADEMY_API_BASE, "auth/logout", { body: {}, csrfToken });
      lastCsrfToken = undefined;
      try {
        window.sessionStorage.removeItem(lockKey);
      } catch {
        /* The in-memory view stays locked until navigation. */
      }
      const target = new URL(accessUrl(), window.location.origin);
      target.searchParams.set("signed_out", "1");
      target.searchParams.set("client_id", audience);
      window.location.assign(target.toString());
    } catch (error) {
      dispatch("academy:auth-error", error);
      // The UI remains locked and offers a retry; callers used as click handlers need no catch.
    }
  }
  return { getSession, clearSession, requireFrontendSession, redirectToSignIn, signOut };
}
